import axios from "axios";

// Token storage. Wave-12 dev-auth flow: browser POSTs to /api/v1/dev/login
// (platform-security's DevLoginController, active under local/standalone),
// stores the HS256 JWT in localStorage, and the request interceptor below
// attaches it as a Bearer header. When datumcraft.security.enabled=false (the
// default in local/standalone) the server ignores the header anyway, so
// the same code path works whether security is gated or not.
const TOKEN_KEY = "curate.auth.token";

export function setAuthToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

export const api = axios.create({
  baseURL: "/api/v1/curate",
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Platform endpoints (license, health, config) live OUTSIDE the curate
// namespace so they can be shared by every product. Separate axios
// instance keeps `api` callers free of the namespace prefix.
export const platformApi = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
});

platformApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ----- Platform DTOs (mirror Java records in platform-licensing /
//       curate-api's CurateConfigController) ---------------------------

export type LicenseStatus = {
  ok: boolean;
  failureReason?: string | null;
  failureMessage?: string | null;
  customerName?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  daysRemaining: number;
  fingerprint: string;
};

export type ConfigResponse = {
  llmProvider: string;
  productVersion: string;
  featureFlags: Record<string, boolean>;
};

// ----- Domain types — mirror the Java records in curate-core -----

export type AssetId = { platform: string; value: string };
export type UserId = { platform: string; value: string };

export type AssetKind =
  | "REPORT"
  | "DASHBOARD"
  | "DATASET"
  | "PACKAGE"
  | "DATA_MODULE"
  | "DATA_SOURCE"
  | "FOLDER"
  | "WORKBOOK"
  | "OTHER";

export type Asset = {
  id: AssetId;
  displayName: string;
  kind: AssetKind;
  nativeType: string;
  folderPath: string;
  owner?: UserId | null;
  lastModified?: string | null;
};

export type CleanupAction =
  | "KEEP"
  | "ARCHIVE"
  | "CONSOLIDATE"
  | "REASSIGN_OWNER"
  | "DELETE_HARD";

export type RiskLevel = "LOW" | "MEDIUM" | "HIGH";

export type PlanItemState =
  | "PROPOSED"
  | "APPROVED"
  | "REJECTED"
  | "MODIFIED"
  | "EXECUTED"
  | "REVERTED";

export type HealthScore = {
  assetId: AssetId;
  value: number;
  confidence: number;
  signals: Record<string, number>;
  reasons: string[];
};

export type CleanupCandidate = {
  asset: Asset;
  score: HealthScore;
  action: CleanupAction;
  confidence: number;
  riskLevel: RiskLevel;
  coolingOffDays: number;
  blastRadius?: number | null;
  duplicateCluster?: string | null;
  reasons: string[];
};

export type PlanItem = {
  id: string;
  candidate: CleanupCandidate;
  state: PlanItemState;
};

export type CleanupPlan = {
  id: string;
  createdAt: string;
  scope: { type: string; value: string };
  items: PlanItem[];
  summary: {
    totalItems: number;
    byAction: Partial<Record<CleanupAction, number>>;
    highRiskItems: number;
    duplicateClusters: number;
  };
};

/**
 * Light shape returned by {@code GET /plans} — every field the Plans
 * page renders, none of the heavy {@link PlanItem} array. Open a plan
 * via {@code GET /plans/:id} to load its items.
 */
export type CleanupPlanSummary = {
  id: string;
  createdAt: string;
  scope: { type: string; value: string };
  summary: {
    totalItems: number;
    byAction: Partial<Record<CleanupAction, number>>;
    highRiskItems: number;
    duplicateClusters: number;
  };
};

export type Explanation = {
  text: string;
  provider?: string | null;
  promptVersion?: string | null;
};

// ----- API methods -----

export async function listPlans(tenantId = "_default"): Promise<CleanupPlanSummary[]> {
  const r = await api.get<CleanupPlanSummary[]>("/plans", { params: { tenantId } });
  return r.data;
}

/**
 * Generate (and optionally persist) a CleanupPlan by reading the
 * Cognos audit DB for the named folder. {@code folderPath} can be a
 * full Cognos search-path or {@code *} for everything. Returns the
 * generated plan immediately; with {@code persist=true} the same
 * plan is also saved and shows up in {@link listPlans}.
 */
export async function proposePlanFromScope(
  folderPath: string,
  tenantId = "_default",
  persist = true,
): Promise<CleanupPlan> {
  const r = await api.post<CleanupPlan>("/plans/from-scope", {
    folderPath,
    tenantId,
    persist,
  });
  return r.data;
}

export async function loadPlan(planId: string): Promise<CleanupPlan> {
  const r = await api.get<CleanupPlan>(`/plans/${planId}`);
  return r.data;
}

export async function approveItem(planId: string, itemId: string): Promise<PlanItem> {
  const r = await api.post<PlanItem>(`/plans/${planId}/items/${itemId}/approve`);
  return r.data;
}

export async function rejectItem(planId: string, itemId: string): Promise<PlanItem> {
  const r = await api.post<PlanItem>(`/plans/${planId}/items/${itemId}/reject`);
  return r.data;
}

export async function modifyItem(planId: string, itemId: string): Promise<PlanItem> {
  const r = await api.post<PlanItem>(`/plans/${planId}/items/${itemId}/modify`);
  return r.data;
}

export async function explainItem(item: PlanItem, tenantId = "_default"): Promise<Explanation> {
  const r = await api.post<Explanation>("/explain", { tenantId, item });
  return r.data;
}

// ----- Upgrade-readiness types + methods -----

export type UpgradeTarget =
  | "COGNOS_12_FROM_11"
  | "POWERBI_PREMIUM"
  | "TABLEAU_CLOUD"
  | "QLIK_SENSE";

export type Severity = "BLOCKER" | "WARN" | "INFO";

export type CompatibilityFinding = {
  assetId: AssetId;
  target: UpgradeTarget;
  ruleId: string;
  severity: Severity;
  title: string;
  details: string;
  fixHint: string;
};

export type UpgradeReadinessReport = {
  target: UpgradeTarget;
  scope: string;
  generatedAt: string;
  assetsScanned: number;
  assetsWithIssues: number;
  totalFindings: number;
  truncated: boolean;
  findings: CompatibilityFinding[];
  countsBySeverity: Partial<Record<Severity, number>>;
};

export async function checkUpgradeReadiness(
  folderPath: string | null,
  target: UpgradeTarget,
  minSeverity: Severity | null,
  maxFindings: number | null,
): Promise<UpgradeReadinessReport> {
  const r = await api.post<UpgradeReadinessReport>("/upgrade-readiness", {
    folderPath,
    target,
    minSeverity,
    maxFindings,
  });
  return r.data;
}

/**
 * Stream the readiness scan as a CSV download. Bypasses the wire cap
 * — operators reach for this when they need every finding offline.
 */
export async function downloadUpgradeReadinessCsv(
  folderPath: string | null,
  target: UpgradeTarget,
  minSeverity: Severity | null,
): Promise<void> {
  const r = await api.post("/upgrade-readiness/report.csv", {
    folderPath,
    target,
    minSeverity,
  }, { responseType: "blob" });
  const headers = (r.headers ?? {}) as Record<string, unknown>;
  const cdRaw = headers["content-disposition"];
  const cd = typeof cdRaw === "string" ? cdRaw : "";
  const filenameMatch = cd.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : "curate-upgrade-readiness.csv";
  const blob = r.data instanceof Blob
    ? r.data
    : new Blob([r.data as BlobPart], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** URL the browser can navigate to for the PDF download. */
export function planPdfUrl(planId: string): string {
  return `/api/v1/curate/plans/${planId}/export.pdf`;
}

/** URL the browser can navigate to for the CSV download. */
export function planCsvUrl(planId: string): string {
  return `/api/v1/curate/plans/${planId}/export.csv`;
}

// ----- Plan impact summary + bulk transitions -----

export type PlanImpactSummary = {
  totalItems: number;
  proposedItems: number;
  approvedItems: number;
  rejectedItems: number;
  modifiedItems: number;
  byAction: Partial<Record<CleanupAction, number>>;
  byRisk: Partial<Record<"LOW" | "MEDIUM" | "HIGH", number>>;
  duplicateClusterCount: number;
  estimatedBlastRadius: number;
  highRiskApprovedCount: number;
  earliestCoolingOffComplete: string | null;
};

export async function loadPlanImpact(planId: string): Promise<PlanImpactSummary> {
  const r = await api.get<PlanImpactSummary>(`/plans/${planId}/impact`);
  return r.data;
}

export type BulkAction = "approve" | "reject" | "modify";
export type BulkResponse = { applied: number; skipped: number; requested: number };

export async function bulkTransition(
  planId: string,
  action: BulkAction,
  itemIds: string[],
): Promise<BulkResponse> {
  const r = await api.post<BulkResponse>(`/plans/${planId}/bulk`, { action, itemIds });
  return r.data;
}
